import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import type { EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Eye, Pencil, Trash2, CalendarDays, ChevronRight, LayoutTemplate } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";
import { MobileFab, FilterChip, FilterChipRow } from "@/components/mobile-list-chrome";
import { useAuth } from "@/lib/auth-context";
import { useGridPreferences } from "@/lib/use-grid-preferences";

// Tinted date-block background per event type for the mobile event card.
const dateBlockTint: Record<string, string> = {
  match_day: "bg-blue-50 text-blue-700",
  practice: "bg-green-50 text-green-700",
  tryout: "bg-orange-50 text-orange-700",
};

const typeBadge: Record<string, string> = {
  match_day: "rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  practice: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400",
  tryout: "rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
};

const courtNames = (e: EventPublic) =>
  e.courts ? [...e.courts].sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.name).join(", ") : "";

export default function EventsPage() {
  const { user: authUser } = useAuth();
  const { schoolId } = useParams<{ schoolId: string }>();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("events");

  const typeFilter = searchParams.get("type") ?? "all";
  const whenFilter = searchParams.get("when") ?? "upcoming";
  const [schoolName, setSchoolName] = useState("");
  const [selected, setSelected] = useState<EventPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");

  function setTypeFilter(v: string) {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); if (v === "all") next.delete("type"); else next.set("type", v); next.delete("page"); return next; }, { replace: true });
  }
  function setWhenFilter(v: string) {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); if (v === "upcoming") next.delete("when"); else next.set("when", v); next.delete("page"); return next; }, { replace: true });
  }

  useEffect(() => {
    if (!schoolId) return;
    api.getSchool(schoolId).then((res) => setSchoolName(res.school.name)).catch(() => {});
  }, [schoolId]);

  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: EventPublic[]; total: number }> => {
      if (!schoolId) return { data: [], total: 0 };
      const res = await api.getEvents(schoolId, {
        type: typeFilter !== "all" ? typeFilter : undefined,
        when: whenFilter,
        search: params.search, sort: params.sort, order: params.order,
        page: params.page, per_page: params.per_page,
      });
      return { data: res.events, total: res.total ?? res.events.length };
    },
    [schoolId, typeFilter, whenFilter],
  );

  const fetchKey = [schoolId ?? "", typeFilter, whenFilter, refreshTick].join("|");

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await api.deleteEvent(id);
      if (selected?.id === id) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  const columns: DataTableColumn<EventPublic>[] = [
    { key: "name", header: "Name", sortable: true, className: "font-medium", render: (e) => e.name },
    { key: "event_type", header: "Type", sortable: true, render: (e) => <span className={typeBadge[e.event_type] ?? ""}>{e.event_type.replace("_", " ")}</span> },
    { key: "court", header: "Court", className: "text-muted-foreground", render: courtNames },
    { key: "starts_at", header: "Date / Time", sortable: true, className: "text-muted-foreground", render: (e) => new Date(e.starts_at).toLocaleString() },
    { key: "matches", header: "Matches", className: "text-muted-foreground", render: (e) => e.match_count },
    {
      key: "actions", header: "", headerClassName: "w-36", cardRole: "action",
      render: (event) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {event._actions.includes("view") && (
            <Link to={`/events/${event.id}`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="view">
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {event._actions.includes("edit") && (
            <Link to={`/events/${event.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="edit">
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {event._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)} data-action="delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Bespoke mobile event card: tinted date block, name, type badge + match
  // count, time/venue, chevron. Whole card links to the detail screen.
  const renderEventCard = (e: EventPublic) => {
    const d = new Date(e.starts_at);
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const venue = courtNames(e);
    return (
      <Link
        to={`/events/${e.id}`}
        className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm transition-transform active:scale-[.99]"
        data-action="view"
      >
        <div className={`flex w-12 shrink-0 flex-col items-center rounded-xl py-2 ${dateBlockTint[e.event_type] ?? "bg-muted text-foreground"}`}>
          <span className="text-lg font-extrabold leading-none">{d.getDate()}</span>
          <span className="text-[10px] font-semibold uppercase">{d.toLocaleDateString(undefined, { month: "short" })}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{e.name}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <span className={typeBadge[e.event_type] ?? ""}>{e.event_type.replace("_", " ")}</span>
            {e.match_count > 0 && (
              <span className="text-xs text-muted-foreground">
                {e.match_count} {e.match_count === 1 ? "match" : "matches"}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {time}
            {venue ? ` · ${venue}` : ""}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />
      </Link>
    );
  };

  return (
    <div className="flex h-full gap-0" data-page="events-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to={`/schools/${schoolId}`} className="hover:underline">{schoolName || "School"}</Link>
              <span>/</span>
              <span>Events</span>
            </div>
            <h1 className="text-2xl font-semibold">Events</h1>
          </div>
          <div className="flex gap-2">
            <Link to={`/schools/${schoolId}/calendar`} className={buttonVariants({ variant: "outline", size: "sm" })}>
              <CalendarDays className="mr-2 h-4 w-4" />
              Calendar
            </Link>
            {canManage && (
              <Link to={`/schools/${schoolId}/match-day-templates`} className={buttonVariants({ variant: "outline", size: "sm" })} data-action="templates">
                <LayoutTemplate className="mr-2 h-4 w-4" />
                Templates
              </Link>
            )}
            {canManage && (
              <Link to={`/schools/${schoolId}/events/new`} className={buttonVariants({ size: "sm" })} data-action="create">
                <Plus className="mr-2 h-4 w-4" />
                New Event
              </Link>
            )}
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        {/* Filters (desktop) */}
        <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center" data-region="filters">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-field="type"
            aria-label="Filter by event type"
          >
            <option value="all">All Types</option>
            <option value="match_day">Match Days</option>
            <option value="practice">Practices</option>
            <option value="tryout">Tryouts</option>
          </select>
          <div className="flex gap-1">
            <Button variant={whenFilter === "upcoming" ? "default" : "outline"} size="sm" onClick={() => setWhenFilter("upcoming")}>Upcoming</Button>
            <Button variant={whenFilter === "past" ? "default" : "outline"} size="sm" onClick={() => setWhenFilter("past")}>Past</Button>
          </div>
        </div>

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={fetchKey}
            rowKey={(e) => e.id}
            rowProps={(e) => ({ "data-item": "event", "data-item-id": e.id })}
            rowClassName={(e) => (selected?.id === e.id ? "bg-muted" : undefined)}
            searchPlaceholder="Search events..."
            defaultSort="starts_at"
            defaultOrder={whenFilter === "past" ? "desc" : "asc"}
            emptyMessage={`No ${whenFilter} events found`}
            onRowClick={(e) => setSelected(e)}
            onStateChange={setTableState}
            renderCard={renderEventCard}
            toolbarExtra={
              <FilterChipRow className="md:hidden">
                <FilterChip active={whenFilter === "upcoming"} onClick={() => setWhenFilter("upcoming")}>Upcoming</FilterChip>
                <FilterChip active={whenFilter === "past"} onClick={() => setWhenFilter("past")}>Past</FilterChip>
                <FilterChip active={typeFilter === "all"} onClick={() => setTypeFilter("all")}>All</FilterChip>
                <FilterChip active={typeFilter === "match_day"} onClick={() => setTypeFilter("match_day")}>Matches</FilterChip>
                <FilterChip active={typeFilter === "practice"} onClick={() => setTypeFilter("practice")}>Practice</FilterChip>
                <FilterChip active={typeFilter === "tryout"} onClick={() => setTypeFilter("tryout")}>Tryouts</FilterChip>
              </FilterChipRow>
            }
          />
        </div>
      </div>
      {canManage && <MobileFab to={`/schools/${schoolId}/events/new`} label="New event" />}
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Event">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Type">
              <span className={typeBadge[selected.event_type] ?? ""}>{selected.event_type.replace("_", " ")}</span>
            </PreviewField>
            <PreviewField label="Date">{new Date(selected.starts_at).toLocaleString()}</PreviewField>
            {selected.courts?.length > 0 && <PreviewField label="Court">{courtNames(selected)}</PreviewField>}
            <PreviewField label="Matches">{selected.match_count}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
